import React from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { Check, Save, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";

import "./LenderManualLoanCaseLayout.css";

import {
  fetchManualLoanCase,
  saveManualLoanCaseDraft,
  finalizeManualLoanCase,
  uploadManualPropertyPhotos,
  deleteManualPropertyPhoto,
  fetchManualPropertyPhotoBlob,
} from "./LenderManualLoanCaseApi";

import { useLenderNavigationGuard } from "../../../../components/layout/lender/LenderNavigationGuardContext";

const MANUAL_STEPS = [
  {
    number: 1,
    key: "personal-info",
    backendCode: "PERSONAL",
    sectionKey: "personal_info",
    translationKey: "steps.personalInfo",
  },
  {
    number: 2,
    key: "financial-info",
    backendCode: "FINANCIAL",
    sectionKey: "financial_info",
    translationKey: "steps.financialInfo",
  },
  {
    number: 3,
    key: "documents",
    backendCode: "DOCUMENTS",
    sectionKey: "documents",
    translationKey: "steps.documents",
  },
  {
    number: 4,
    key: "property-info",
    backendCode: "PROPERTY",
    sectionKey: "property_info",
    translationKey: "steps.propertyInfo",
  },
  {
    number: 5,
    key: "final-loan-details",
    backendCode: "FINAL_LOAN",
    sectionKey: "final_loan_details",
    translationKey: "steps.finalLoanDetails",
  },
  {
    number: 6,
    key: "review-save",
    backendCode: "REVIEW",
    sectionKey: "review",
    translationKey: "steps.reviewSave",
  },
];

function isObject(value) {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function valuesAreEqual(firstValue, secondValue) {
  return (
    JSON.stringify(firstValue ?? null) === JSON.stringify(secondValue ?? null)
  );
}

function readStoredDraft(storageKey) {
  if (typeof window === "undefined" || !storageKey) {
    return null;
  }

  try {
    const storedValue = window.localStorage.getItem(storageKey);

    if (!storedValue) {
      return null;
    }

    const parsed = JSON.parse(storedValue);

    return isObject(parsed) ? parsed : null;
  } catch (error) {
    console.error("Failed to read the manual-entry draft:", error);

    return null;
  }
}

function getSectionValue(stepValues, step) {
  const stepDraft = stepValues?.[step.key];

  if (stepDraft === null || stepDraft === undefined) {
    return undefined;
  }

  if (
    isObject(stepDraft) &&
    Object.prototype.hasOwnProperty.call(stepDraft, step.sectionKey)
  ) {
    return stepDraft[step.sectionKey];
  }

  return stepDraft;
}

function buildAllStepsPayload({ stepValues, currentStep }) {
  const payload = {
    manual_entry_current_step: currentStep?.backendCode || "PERSONAL",
  };

  MANUAL_STEPS.forEach((step) => {
    const sectionValue = getSectionValue(stepValues, step);

    if (sectionValue !== undefined) {
      payload[step.sectionKey] = sectionValue;
    }
  });

  const finalStepDraft = stepValues?.["final-loan-details"];

  const finalLoanDetails =
    finalStepDraft?.final_loan_details || finalStepDraft || null;

  const fundingSources = Array.isArray(finalStepDraft?.funding_sources)
    ? finalStepDraft.funding_sources
    : Array.isArray(finalLoanDetails?.funding_sources)
      ? finalLoanDetails.funding_sources
      : [];

  payload.funding_sources = fundingSources;

  return payload;
}

function mergeLoanCaseResponse(previous, updated) {
  if (!updated) {
    return previous;
  }

  return {
    ...(previous || {}),
    ...updated,

    dossier: {
      ...(previous?.dossier || {}),
      ...(updated?.dossier || {}),
    },
  };
}

export default function LenderManualLoanCaseLayout() {
  const { t } = useTranslation("lender-manual-loan-case-layout");

  const { getAccessTokenSilently } = useAuth0();

  const navigate = useNavigate();
  const location = useLocation();

  const { loanCaseId } = useParams();
  const [finalizationResult, setFinalizationResult] = React.useState(null);

  const { requestNavigation, registerNavigationGuard } =
    useLenderNavigationGuard();

  const draftStorageKey = `legup_manual_loan_case_draft_${loanCaseId}`;

  const initialStoredDraftRef = React.useRef(readStoredDraft(draftStorageKey));

  const registeredStepKeysRef = React.useRef(new Set());

  const [stepValues, setStepValues] = React.useState(() => {
    const storedDraft = initialStoredDraftRef.current;

    return isObject(storedDraft?.stepValues) ? storedDraft.stepValues : {};
  });

  const [validationRequest, setValidationRequest] = React.useState(0);

  const [stepValidity, setStepValidity] = React.useState(() => {
    const storedDraft = initialStoredDraftRef.current;

    return isObject(storedDraft?.stepValidity) ? storedDraft.stepValidity : {};
  });

  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(
    Boolean(initialStoredDraftRef.current?.isDirty),
  );

  const [loanCaseData, setLoanCaseData] = React.useState(null);

  const [isLoading, setIsLoading] = React.useState(true);

  const [isSaving, setIsSaving] = React.useState(false);

  const [error, setError] = React.useState(null);

  const [toast, setToast] = React.useState({
    open: false,
    closing: false,
    title: "",
    message: "",
  });

  const [leaveModal, setLeaveModal] = React.useState({
    open: false,
    targetPath: null,
    navigateOptions: null,
  });

  const errorMessage = React.useMemo(() => {
    if (!error) {
      return "";
    }

    if (typeof error === "string") {
      return error;
    }

    if (error.message) {
      return error.message;
    }

    return t(error.translationKey, error.fallback);
  }, [error, t]);

  const steps = React.useMemo(
    () =>
      MANUAL_STEPS.map((step) => ({
        ...step,

        label: t(step.translationKey),

        path: `/lender/loan-cases/${loanCaseId}` + `/manual-entry/${step.key}`,
      })),
    [loanCaseId, t],
  );

  const normalizedPathname = React.useMemo(
    () => location.pathname.replace(/\/+$/, ""),
    [location.pathname],
  );

  const currentStepIndex = React.useMemo(
    () => steps.findIndex((step) => step.path === normalizedPathname),
    [normalizedPathname, steps],
  );

  const currentStep =
    currentStepIndex >= 0 ? steps[currentStepIndex] : steps[0];

  const currentStepValidity = stepValidity?.[currentStep?.key];

  const wizardBasePath = `/lender/loan-cases/${loanCaseId}` + "/manual-entry";

  const canGoBack = currentStepIndex > 0;

  const canGoNext =
    currentStepIndex >= 0 && currentStepIndex < steps.length - 1;

  const isLastStep = currentStepIndex === steps.length - 1;

  const showToast = React.useCallback((title, message) => {
    setToast({
      open: true,
      closing: false,
      title,
      message,
    });
  }, []);

  React.useEffect(() => {
    if (!toast.open) {
      return undefined;
    }

    const closingTimer = window.setTimeout(() => {
      setToast((previous) => ({
        ...previous,
        closing: true,
      }));
    }, 2200);

    const removeTimer = window.setTimeout(() => {
      setToast({
        open: false,
        closing: false,
        title: "",
        message: "",
      });
    }, 2420);

    return () => {
      window.clearTimeout(closingTimer);

      window.clearTimeout(removeTimer);
    };
  }, [toast.open]);

  const loadLoanCase = React.useCallback(
    async ({ showLoading = true } = {}) => {
      try {
        if (showLoading) {
          setIsLoading(true);
        }

        setError(null);

        const accessToken = await getAccessTokenSilently();

        const data = await fetchManualLoanCase({
          loanCaseId,
          accessToken,
        });

        setLoanCaseData(data);

        return data;
      } catch (requestError) {
        console.error("Failed to load manual loan case:", requestError);

        setError({
          message: requestError?.message || "",
          translationKey: "states.loadFailed",
          fallback: "The loan case could not be loaded.",
        });

        return null;
      } finally {
        if (showLoading) {
          setIsLoading(false);
        }
      }
    },
    [getAccessTokenSilently, loanCaseId],
  );

  React.useEffect(() => {
    loadLoanCase();
  }, [loadLoanCase]);

  /*
   * Restore the correct local draft when the loan-case ID changes
   * without requiring a complete page reload.
   */
  React.useEffect(() => {
    const storedDraft = readStoredDraft(draftStorageKey);

    setStepValues(
      isObject(storedDraft?.stepValues) ? storedDraft.stepValues : {},
    );

    setStepValidity(
      isObject(storedDraft?.stepValidity) ? storedDraft.stepValidity : {},
    );
    setFinalizationResult(null);
    setHasUnsavedChanges(Boolean(storedDraft?.isDirty));

    registeredStepKeysRef.current = new Set();

    setLeaveModal({
      open: false,
      targetPath: null,
      navigateOptions: null,
    });
  }, [draftStorageKey]);

  React.useEffect(() => {
    if (!loanCaseId || typeof window === "undefined") {
      return;
    }

    if (finalizationResult) {
      window.localStorage.removeItem(draftStorageKey);

      return;
    }

    try {
      window.localStorage.setItem(
        draftStorageKey,
        JSON.stringify({
          loanCaseId,
          stepValues,
          stepValidity,
          isDirty: hasUnsavedChanges,
          updatedAt: new Date().toISOString(),
        }),
      );
    } catch (storageError) {}
  }, [
    draftStorageKey,
    finalizationResult,
    hasUnsavedChanges,
    loanCaseId,
    stepValidity,
    stepValues,
  ]);

  /*
   * Redirect the manual-entry root route to the backend's
   * last recorded step.
   */
  React.useEffect(() => {
    if (normalizedPathname !== wizardBasePath) {
      return;
    }

    const backendStep = loanCaseData?.manual_entry_current_step || "PERSONAL";

    const matchingStep =
      steps.find((step) => step.backendCode === backendStep) || steps[0];

    navigate(matchingStep.path, {
      replace: true,
    });
  }, [loanCaseData, navigate, normalizedPathname, steps, wizardBasePath]);

  const handleStepValidityChange = React.useCallback((stepKey, isValid, missingFields = []) => {
    setStepValidity((previous) => {
      const next = {
        isValid: Boolean(isValid),
        missingFields: Array.isArray(missingFields) ? missingFields : [],
      };
      if (valuesAreEqual(previous?.[stepKey], next)) return previous;
      return { ...previous, [stepKey]: next };
    });
  }, []);

  const handleStepValuesChange = React.useCallback((stepKey, values) => {
    setStepValues((previous) => {
      const previousValues = previous?.[stepKey];

      const isFirstRegistration = !registeredStepKeysRef.current.has(stepKey);

      if (isFirstRegistration) {
        registeredStepKeysRef.current.add(stepKey);

        if (valuesAreEqual(previousValues, values)) {
          return previous;
        }

        return {
          ...previous,
          [stepKey]: values,
        };
      }

      if (valuesAreEqual(previousValues, values)) {
        return previous;
      }

      setHasUnsavedChanges(true);

      return {
        ...previous,
        [stepKey]: values,
      };
    });
  }, []);

  /*
   * This guard is used by every component that navigates through
   * LenderNavigationGuardContext, including the platform sidebar.
   */
  const handleGuardedNavigation = React.useCallback(
    ({ targetPath, navigateOptions = {} }) => {
      const targetPathname = String(targetPath)
        .split("?")[0]
        .split("#")[0]
        .replace(/\/+$/, "");

      const remainsInsideWizard =
        targetPathname === wizardBasePath ||
        targetPathname.startsWith(`${wizardBasePath}/`);

      /*
       * Step-to-step navigation remains unrestricted.
       */
      if (remainsInsideWizard || !hasUnsavedChanges) {
        return false;
      }

      setError(null);

      setLeaveModal({
        open: true,
        targetPath,
        navigateOptions,
      });

      return true;
    },
    [hasUnsavedChanges, wizardBasePath],
  );

  const uploadPropertyPhotos = React.useCallback(
    async ({ loanCaseId: requestedLoanCaseId, files }) => {
      const accessToken = await getAccessTokenSilently();

      return uploadManualPropertyPhotos({
        loanCaseId: requestedLoanCaseId || loanCaseId,
        files,
        accessToken,
      });
    },
    [getAccessTokenSilently, loanCaseId],
  );

  const deletePropertyPhoto = React.useCallback(
    async ({ loanCaseId: requestedLoanCaseId, photoId }) => {
      const accessToken = await getAccessTokenSilently();

      return deleteManualPropertyPhoto({
        loanCaseId: requestedLoanCaseId || loanCaseId,
        photoId,
        accessToken,
      });
    },
    [getAccessTokenSilently, loanCaseId],
  );

  const fetchPropertyPhotoBlob = React.useCallback(
    async ({ loanCaseId: requestedLoanCaseId, photoId }) => {
      const accessToken = await getAccessTokenSilently();

      return fetchManualPropertyPhotoBlob({
        loanCaseId: requestedLoanCaseId || loanCaseId,
        photoId,
        accessToken,
      });
    },
    [getAccessTokenSilently, loanCaseId],
  );

  React.useEffect(() => {
    return registerNavigationGuard(handleGuardedNavigation);
  }, [handleGuardedNavigation, registerNavigationGuard]);

  const handleStepNavigation = React.useCallback(
    (targetPath) => {
      setError(null);

      requestNavigation(targetPath);
    },
    [requestNavigation],
  );

  const handleBack = React.useCallback(() => {
    if (!canGoBack) {
      return;
    }

    setError(null);

    requestNavigation(steps[currentStepIndex - 1].path);
  }, [canGoBack, currentStepIndex, requestNavigation, steps]);

  const handleNext = React.useCallback(() => {
    if (!canGoNext) {
      return;
    }

    if (currentStepValidity?.isValid !== true) {
      setValidationRequest((previous) => previous + 1);
      const missing = currentStepValidity?.missingFields || [];
      setError({
        message: missing.length
          ? `Please review: ${missing.join(", ")}.`
          : t("states.invalidStep", "Complete all required fields before continuing."),
      });

      return;
    }

    setError(null);

    requestNavigation(steps[currentStepIndex + 1].path);
  }, [
    canGoNext,
    currentStepIndex,
    currentStepValidity,
    requestNavigation,
    steps,
    t,
  ]);

  /*
   * Saves every populated manual-entry section.
   */
  const handleSaveDraft = React.useCallback(async () => {
    try {
      setIsSaving(true);
      setError(null);

      const accessToken = await getAccessTokenSilently();

      const payload = buildAllStepsPayload({
        stepValues,
        currentStep,
      });

      const updated = await saveManualLoanCaseDraft({
        loanCaseId,
        payload,
        accessToken,
      });

      setLoanCaseData((previous) => mergeLoanCaseResponse(previous, updated));

      setHasUnsavedChanges(false);

      const timestamp = new Date().toISOString();

      window.localStorage.setItem(
        draftStorageKey,
        JSON.stringify({
          loanCaseId,
          stepValues,
          stepValidity,
          isDirty: false,
          savedAt: timestamp,
          updatedAt: timestamp,
        }),
      );

      showToast(
        t("toast.savedTitle", "Draft saved"),
        t(
          "toast.savedMessage",
          "All manual-entry changes were saved successfully.",
        ),
      );

      return updated;
    } catch (requestError) {
      console.error("Failed to save manual-entry draft:", requestError);

      setError({
        message: requestError?.message || "",
        translationKey: "states.saveFailed",
        fallback: "The draft could not be saved.",
      });

      return null;
    } finally {
      setIsSaving(false);
    }
  }, [
    currentStep,
    draftStorageKey,
    getAccessTokenSilently,
    loanCaseId,
    showToast,
    stepValidity,
    stepValues,
    t,
  ]);

  const closeLeaveModal = React.useCallback(() => {
    setLeaveModal({
      open: false,
      targetPath: null,
      navigateOptions: null,
    });
  }, []);

  const handleStayOnPage = React.useCallback(() => {
    closeLeaveModal();
  }, [closeLeaveModal]);

  const handleDiscardAndLeave = React.useCallback(() => {
    const targetPath = leaveModal.targetPath;

    const navigateOptions = leaveModal.navigateOptions || {};

    window.localStorage.removeItem(draftStorageKey);

    setHasUnsavedChanges(false);

    closeLeaveModal();

    if (targetPath) {
      navigate(targetPath, navigateOptions);
    }
  }, [
    closeLeaveModal,
    draftStorageKey,
    leaveModal.navigateOptions,
    leaveModal.targetPath,
    navigate,
  ]);

  const handleSaveAndLeave = React.useCallback(async () => {
    const targetPath = leaveModal.targetPath;

    const navigateOptions = leaveModal.navigateOptions || {};

    const saved = await handleSaveDraft();

    if (!saved || !targetPath) {
      return;
    }

    closeLeaveModal();

    navigate(targetPath, navigateOptions);
  }, [
    closeLeaveModal,
    handleSaveDraft,
    leaveModal.navigateOptions,
    leaveModal.targetPath,
    navigate,
  ]);

  const handleFinalizeCase = React.useCallback(async () => {
    if (currentStepValidity?.isValid !== true) {
      setError({
        translationKey: "states.invalidReview",

        fallback: "Review the case before saving it.",
      });

      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const accessToken = await getAccessTokenSilently();

      const payload = buildAllStepsPayload({
        stepValues,
        currentStep,
      });

      const finalized = await finalizeManualLoanCase({
        loanCaseId,
        payload,
        accessToken,
      });

      setLoanCaseData((previous) => mergeLoanCaseResponse(previous, finalized));

      setHasUnsavedChanges(false);
      window.localStorage.removeItem(draftStorageKey);

      setFinalizationResult(finalized);

      showToast(
        t("toast.completedTitle", "Loan case saved"),
        t(
          "toast.completedMessage",
          "The manual loan case was completed successfully.",
        ),
      );
    } catch (requestError) {
      console.error("Failed to finalize manual loan case:", requestError);

      setError({
        message: requestError?.message || "",
        translationKey: "states.finalizeFailed",
        fallback: "The loan case could not be saved.",
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    currentStep,
    currentStepValidity,
    draftStorageKey,
    getAccessTokenSilently,
    loanCaseId,
    showToast,
    stepValues,
    t,
  ]);

  const handleFinalizationSuccessClose = React.useCallback(() => {
    const completedLoanCaseId = finalizationResult?.loan_case_id || loanCaseId;

    setFinalizationResult(null);

    navigate("/lender/loan-cases", {
      state: {
        refreshLoanCases: true,
        completedLoanCaseId,
      },
    });
  }, [finalizationResult, loanCaseId, navigate]);

  React.useEffect(() => {
    function handleBeforeUnload(event) {
      if (!hasUnsavedChanges) {
        return;
      }

      event.preventDefault();
      event.returnValue = "";
    }

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  React.useEffect(() => {
    if (!finalizationResult) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [finalizationResult]);

  if (isLoading) {
    return (
      <div className="lender-review-layout">
        <div className="lender-review-layout__state">
          {t("states.loading", "Loading loan case...")}
        </div>
      </div>
    );
  }

  if (errorMessage && !loanCaseData) {
    return (
      <div className="lender-review-layout">
        <div className="lender-review-layout__state lender-review-layout__state--error">
          {errorMessage}
        </div>
      </div>
    );
  }

  return (
    <div className="lender-review-layout">
      <div className="lender-review-layout__shell">
        <div className="lender-review-layout__topbar">
          <button
            type="button"
            className="lender-review-layout__crumb-link"
            onClick={() => handleStepNavigation("/lender/loan-cases")}
          >
            {t("breadcrumbs.loanCases", "Loan cases")}
          </button>

          <span className="lender-review-layout__crumb-separator">›</span>

          <span className="lender-review-layout__crumb-code">
            {loanCaseData?.case_reference || "—"}
          </span>
        </div>

        <div className="lender-review-layout__panel">
          <aside className="lender-review-layout__sidebar">
            <div className="lender-review-layout__sidebar-title">
              {t("sidebar.title", "Manual entry")}
            </div>

            <div className="lender-review-layout__steps">
              {steps.map((step) => (
                <button
                  key={step.path}
                  type="button"
                  title={stepValidity?.[step.key]?.isValid === false
                    ? `Missing: ${(stepValidity[step.key].missingFields || []).join(", ") || "Required fields"}`
                    : step.label}
                  aria-label={`${step.label}${stepValidity?.[step.key]?.isValid === false ? ", incomplete" : ""}`}
                  className={`lender-review-layout__step ${
                    normalizedPathname === step.path
                      ? "lender-review-layout__step--active"
                      : ""
                  }`}
                  onClick={() => handleStepNavigation(step.path)}
                  disabled={isSaving}
                >
                  <span className="lender-review-layout__step-active-rail" />

                  <span className="lender-review-layout__step-number">
                    {step.number}
                  </span>

                  <span className="lender-review-layout__step-label">
                    {step.label}
                  </span>
                  {stepValidity?.[step.key]?.isValid === false ? (
                    <span className="lender-review-layout__step-status lender-review-layout__step-status--missing"
                      title={(stepValidity[step.key].missingFields || []).join(", ") || "Incomplete step"}>
                      <TriangleAlert size={13} />
                    </span>
                  ) : stepValidity?.[step.key]?.isValid === true ? (
                    <span className="lender-review-layout__step-status lender-review-layout__step-status--complete"><Check size={14}/></span>
                  ) : null}
                </button>
              ))}
            </div>
          </aside>

          <section className="lender-review-layout__main">
            <div className="lender-review-layout__header">
              <h1 className="lender-review-layout__title">
                {currentStep?.label || "—"}
              </h1>

              <div className="lender-review-layout__actions">
                <button
                  type="button"
                  className="lender-review-layout__btn lender-review-layout__btn--save-draft"
                  onClick={handleSaveDraft}
                  disabled={isSaving}
                  aria-busy={isSaving}
                >
                  <Save size={16} />

                  {isSaving
                    ? t("buttons.saving", "Saving...")
                    : t("buttons.saveDraft", "Save draft")}
                </button>
              </div>
            </div>

            {errorMessage ? (
              <div className="lender-review-layout__inline-error">
                {errorMessage}
              </div>
            ) : null}

            {isLastStep && steps.some(step => step.key !== "review-save" && stepValidity?.[step.key]?.isValid === false) ? (
              <div className="lender-review-layout__missing-summary" role="status">
                <strong>{t("states.incompleteSections", "Sections requiring attention")}</strong>
                {steps.filter(step => step.key !== "review-save" && stepValidity?.[step.key]?.isValid === false).map(step => (
                  <button key={step.key} type="button" onClick={() => handleStepNavigation(step.path)}>
                    <TriangleAlert size={14}/>
                    <span>{step.label}: {(stepValidity[step.key].missingFields || []).join(", ") || "Review required fields"}</span>
                  </button>
                ))}
              </div>
            ) : null}

            <div className="lender-review-layout__content-card">
              <Outlet
                context={{
                  loanCaseData,
                  loanCaseId,

                  dossierId: loanCaseData?.dossier_id,

                  currentStepKey: currentStep?.key || null,

                  currentSectionKey: currentStep?.sectionKey || null,

                  currentBackendStep: currentStep?.backendCode || null,

                  isManualEntry: true,
                  isEditMode: true,
                  isRequestMode: false,

                  stepValues,
                  stepValidity,
                  validationRequest,

                  handleStepValidityChange,
                  handleStepValuesChange,

                  uploadPropertyPhotos,
                  deletePropertyPhoto,
                  fetchPropertyPhotoBlob,

                  refreshLoanCase: loadLoanCase,
                  finalizationResult,
                  onFinalizationSuccessClose: handleFinalizationSuccessClose,
                }}
              />
            </div>

            <div className="lender-review-layout__footer-area">
              {errorMessage && isLastStep ? (
                <div
                  className="lender-review-layout__footer-error"
                  role="alert"
                  aria-live="assertive"
                >
                  <TriangleAlert size={19} strokeWidth={2.2} />

                  <div className="lender-review-layout__footer-error-copy">
                    <strong>
                      {t("states.couldNotSave", "The case could not be saved")}
                    </strong>

                    <span>{errorMessage}</span>
                  </div>
                </div>
              ) : null}

              <div className="lender-review-layout__footer">
                <button
                  type="button"
                  className="lender-review-layout__footer-btn lender-review-layout__footer-btn--back"
                  onClick={handleBack}
                  disabled={!canGoBack || isSaving}
                >
                  {t("buttons.back", "Back")}
                </button>

                {isLastStep ? (
                  <button
                    type="button"
                    className="lender-review-layout__footer-btn lender-review-layout__footer-btn--submit"
                    onClick={handleFinalizeCase}
                    disabled={
                      isSaving ||
                      Boolean(finalizationResult) ||
                      currentStepValidity?.isValid !== true
                    }
                  >
                    <Save size={17} />

                    <span>
                      {isSaving
                        ? t("buttons.saving", "Saving...")
                        : t("buttons.saveCase", "Save case")}
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="lender-review-layout__footer-btn lender-review-layout__footer-btn--primary"
                    onClick={handleNext}
                    disabled={!canGoNext || isSaving}
                  >
                    {t("buttons.nextStep", "Next step")}
                  </button>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>

      {toast.open ? (
        <div
          className={`lender-review-layout__toast ${
            toast.closing ? "lender-review-layout__toast--closing" : ""
          }`}
          role="status"
          aria-live="polite"
        >
          <div className="lender-review-layout__toast-icon">
            <Check size={20} strokeWidth={2.6} />
          </div>

          <div className="lender-review-layout__toast-copy">
            <div className="lender-review-layout__toast-title">
              {toast.title}
            </div>

            <div className="lender-review-layout__toast-text">
              {toast.message}
            </div>
          </div>
        </div>
      ) : null}

      {leaveModal.open ? (
        <div
          className="lender-review-layout__modal-backdrop"
          role="presentation"
        >
          <div
            className="lender-review-layout__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="manual-leave-modal-title"
            aria-describedby="manual-leave-modal-text"
          >
            <div className="lender-review-layout__modal-icon">
              <TriangleAlert size={24} strokeWidth={2.2} />
            </div>

            <h3
              id="manual-leave-modal-title"
              className="lender-review-layout__modal-title"
            >
              {t("leaveModal.title", "Save changes before leaving?")}
            </h3>

            <p
              id="manual-leave-modal-text"
              className="lender-review-layout__modal-text"
            >
              {t(
                "leaveModal.text",
                "You have changes that have not been saved to the loan case.",
              )}
            </p>

            <div className="lender-review-layout__modal-actions">
              <button
                type="button"
                className="lender-review-layout__modal-btn lender-review-layout__modal-btn--ghost"
                onClick={handleStayOnPage}
                disabled={isSaving}
              >
                {t("leaveModal.stay", "Stay here")}
              </button>

              <button
                type="button"
                className="lender-review-layout__modal-btn lender-review-layout__modal-btn--danger"
                onClick={handleDiscardAndLeave}
                disabled={isSaving}
              >
                {t("leaveModal.discard", "Discard changes")}
              </button>

              <button
                type="button"
                className="lender-review-layout__modal-btn lender-review-layout__modal-btn--save"
                onClick={handleSaveAndLeave}
                disabled={isSaving}
                aria-busy={isSaving}
              >
                <Save size={16} strokeWidth={2.2} />

                {isSaving
                  ? t("leaveModal.saving", "Saving...")
                  : t("leaveModal.saveAndLeave", "Save and leave")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
